// @greenlight-spec: 32-knowledge.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { api } from '@/lib/api';
import type { KnowledgeScope } from '@greenlight/contract';

export interface KnowledgeEntryCreateProps {
  /** Which scope to author in. Integration scope is authored on /integrations. */
  scope: Extract<KnowledgeScope, 'org' | 'app'>;
  open: boolean;
  onClose: () => void;
  /** Called with the new entry id so the caller can open it in the editor. */
  onCreated: (id: string) => void;
}

const SCOPE_LABEL: Record<KnowledgeEntryCreateProps['scope'], string> = {
  org: 'org',
  app: 'app',
};

/**
 * Author a new org- or app-scope Knowledge entry from /knowledge (docs/28
 * §/knowledge → "create"). Integration entries are authored on /integrations,
 * so this drawer only ever handles org and app. The body/visibility/tags are
 * refined afterwards in the shared {@link KnowledgeEntryEditor}; create keeps to
 * the minimum the server requires (topic + title, plus an app for app scope).
 */
export function KnowledgeEntryCreate({
  scope,
  open,
  onClose,
  onCreated,
}: KnowledgeEntryCreateProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const createMutation = api.knowledge.createEntry.useMutation();

  const [appId, setAppId] = React.useState('');
  const [topic, setTopic] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [bodyMd, setBodyMd] = React.useState('');

  // App picker source — only fetched while the app-scope drawer is open.
  const appsKey = ['apps', 'knowledge-create'];
  const appsQuery = api.apps.list.useQuery(
    appsKey,
    { query: { limit: 100 } },
    { queryKey: appsKey, enabled: open && scope === 'app' },
  );
  const apps = appsQuery.data?.status === 200 ? appsQuery.data.body.items : [];

  React.useEffect(() => {
    if (!open) {
      setAppId('');
      setTopic('');
      setTitle('');
      setBodyMd('');
    }
  }, [open]);

  const topicTrimmed = topic.trim();
  const titleTrimmed = title.trim();
  const appReady = scope !== 'app' || appId !== '';
  const canSubmit = topicTrimmed !== '' && titleTrimmed !== '' && appReady;

  async function onCreate(): Promise<void> {
    if (!canSubmit) return;
    try {
      const result = await createMutation.mutateAsync({
        body: {
          scope,
          ...(scope === 'app' ? { appId } : {}),
          topic: topicTrimmed,
          title: titleTrimmed,
          bodyMd,
        },
      });
      if (result.status >= 300) throw result;
      queryClient.invalidateQueries({ queryKey: ['knowledge-entries'] });
      toast.success('Knowledge entry created.');
      onCreated(result.body.id);
      onClose();
    } catch {
      // Network/unexpected failure (or a 4xx the server raised, e.g. duplicate
      // topic) — keep the drawer open so the draft survives, like the dialogs.
      toast.error('Failed to create entry.');
    }
  }

  const titleNode = (
    <div className="min-w-0">
      <span className="font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        New knowledge entry · {SCOPE_LABEL[scope]}
      </span>
      <p className="mt-0.5 text-sm text-muted-foreground">
        {scope === 'app'
          ? 'Architecture notes, runbooks, or gotchas scoped to one app.'
          : 'Reference every agent reads at session start.'}
      </p>
    </div>
  );

  const footerNode = (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button
        variant="ghost"
        onClick={onClose}
        className="h-12 w-full bg-muted/50 sm:h-auto sm:w-auto sm:bg-transparent"
      >
        Cancel
      </Button>
      <Button
        onClick={onCreate}
        disabled={!canSubmit || createMutation.isPending}
        data-testid="entry-create-submit"
        className="h-12 w-full sm:h-auto sm:w-auto"
      >
        {createMutation.isPending ? 'Creating…' : 'Create entry'}
      </Button>
    </div>
  );

  return (
    <GLDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      variant="panel"
      mobileDragResetKey={open}
      defaultWidth={640}
      minWidth={360}
      storageKey="gl-knowledge-entry-create-width"
      title={titleNode}
      srDescription="Create a knowledge entry"
      mobileClassName="max-h-[92dvh] rounded-t-[20px]"
      contentProps={{ 'data-testid': 'knowledge-entry-create' }}
      footer={footerNode}
    >
      <div className="space-y-4">
        {scope === 'app' && (
          <div className="space-y-1.5">
            <Label htmlFor="ke-app">App</Label>
            {appsQuery.isError ? (
              <Alert variant="destructive" data-testid="entry-create-apps-error">
                <AlertDescription>Couldn&apos;t load apps. Retry in a moment.</AlertDescription>
              </Alert>
            ) : (
              <Select value={appId} onValueChange={setAppId} disabled={appsQuery.isLoading}>
                <SelectTrigger id="ke-app" data-testid="entry-create-app">
                  <SelectValue
                    placeholder={appsQuery.isLoading ? 'Loading apps…' : 'Select an app'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {apps.map((a) => (
                    <SelectItem key={a.app_id} value={a.app_id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="ke-topic">Topic</Label>
          <input
            id="ke-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. architecture"
            maxLength={63}
            data-testid="entry-create-topic"
            className="w-full rounded-gl border border-border bg-transparent px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          />
          <p className="text-[11px] text-muted-foreground">
            A short slug, unique within this scope. It can&apos;t be changed later.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ke-title">Title</Label>
          <input
            id="ke-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short, human title"
            maxLength={255}
            data-testid="entry-create-title"
            className="w-full rounded-gl border border-border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ke-body">Body (Markdown)</Label>
          <Textarea
            id="ke-body"
            value={bodyMd}
            onChange={(e) => setBodyMd(e.target.value)}
            rows={12}
            placeholder="Write the entry in Markdown. You can refine visibility and tags after creating."
            className="font-mono text-xs"
          />
        </div>
      </div>
    </GLDrawer>
  );
}
